import React, { useEffect, useState } from "react";
import {
  IServiceTicket,
  ServiceTicketType,
  IAddress,
} from "../../models/serviceManagement.model";
import {
  X,
  User,
  Phone,
  MapPin,
  FileText,
  UserCheck,
  ClipboardList,
  ChevronUp,
  ChevronDown,
  MessageSquareWarning,
  Landmark,
  MapPinned,
  LocateFixed,
  QrCode,
  Users,
  AlertTriangle,
  CircuitBoard,
} from "lucide-react";
import Button, { ButtonType } from "../Assets/Button";
import { useDispatch, useSelector } from "react-redux";
import { RootDispatch, RootState } from "../../store";
import {
  getServicePartnersByFilter,
  updateServiceTicket,
  getInverterModelNo,
  getServiceErrorCodes,
} from "../../store/slices/serviceManagementSlice";
import toast from "react-hot-toast";
import { getTalukasByDistrict } from "../../store/slices/kanbanSlice";
import { formatDate } from "../../utils/formatDateUtils";

const EditableField: React.FC<{
  icon: React.ReactNode;
  label: string;
  value?: string | null;
  editValue?: string;
  onChange?: (value: string) => void;
  type?: "text" | "textarea" | "select";
  options?: Array<{ value: string; label: string }>;
  className?: string;
  required?: boolean;
  editMode: boolean;
}> = ({
  icon,
  label,
  value,
  editValue,
  onChange,
  type = "text",
  options,
  className = "",
  required = false,
  editMode,
}) => {
  if (!editMode) {
    if (!value || String(value).trim() === "") return null;
    return (
      <div className={`flex items-start space-x-3 py-3 ${className}`}>
        <div className="flex-shrink-0 w-4 h-4 text-slate-500 mt-0.5">
          {icon}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">
            {label}
          </p>
          <p className="text-sm text-slate-900 font-normal leading-relaxed">
            {value}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={`flex items-start space-x-3 py-3 ${className}`}>
      <div className="flex-shrink-0 w-4 h-4 text-slate-500 mt-0.5">{icon}</div>
      <div className="flex-1 min-w-0">
        <label className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1 block">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
        {type === "select" ? (
          <select
            value={editValue || ""}
            onChange={(e) => onChange?.(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
          >
            <option value="">Select {label}</option>
            {options?.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        ) : (
          <input
            type={type}
            value={editValue || ""}
            onChange={(e) => onChange?.(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
          />
        )}
      </div>
    </div>
  );
};

const SectionHeader: React.FC<{ title: string }> = ({ title }) => (
  <h3 className="text-sm font-semibold text-slate-900 uppercase tracking-wide mb-3 flex items-center">
    <span className="w-1 h-4 bg-slate-300 mr-3"></span>
    {title}
  </h3>
);

interface ServiceTicketCardModalProps {
  isOpen: boolean;
  onClose: () => void;
  ticket: IServiceTicket;
}

const ServiceTicketCardModal: React.FC<ServiceTicketCardModalProps> = ({
  isOpen,
  onClose,
  ticket,
}) => {
  const dispatch = useDispatch<RootDispatch>();

  const [editMode, setEditMode] = useState(false);

  const [formData, setFormData] = useState<Partial<IServiceTicket>>({
    ticketType: ticket.ticketType,
    epcName: ticket.epcName || "",
    epcMobileNumber: ticket.epcMobileNumber || "",
    epcPincode: ticket.epcPincode || "",
    siteAddress: ticket.siteAddress || "",
    customerName: ticket.customerName || "",
    customerMobileNumber: ticket.customerMobileNumber || "",
    customerAddress: {
      addressLine1: ticket.customerAddress?.addressLine1 || "",
      state: ticket.customerAddress?.state || "",
      district: ticket.customerAddress?.district || "",
      pincode: ticket.customerAddress?.pincode || "",
    },
    serialNumber: ticket.serialNumber || "",
    loggerSerialNo: ticket.loggerSerialNo || "",
    complainNote: ticket.complainNote || "",
    inverterModel: ticket.inverterModel || "",
    errorCode: ticket.errorCode || "",
    servicePartner: ticket.servicePartner,
    resolutionType: ticket.resolutionType || undefined,
  });

  const [selectedPhase, setSelectedPhase] = useState<"single" | "three" | "">(
    "",
  );
  const [selectedErrorCode, setSelectedErrorCode] = useState<{
    code: string;
    message: string;
  } | null>(null);

  // Service Partner state
  const [partnerDropdownOpen, setPartnerDropdownOpen] = useState(false);
  const [partnerSearch, setPartnerSearch] = useState("");
  const [, setShowPartnerSearch] = useState(false);
  const [partnerSearchDistrict, setPartnerSearchDistrict] = useState("");
  const [partnerSearchTaluka, setPartnerSearchTaluka] = useState("");
  const [partnerSearchPincode, setPartnerSearchPincode] = useState("");
  const [filteredPartners, setFilteredPartners] = useState<any[]>([]);

  const updateServiceTicketLoading = useSelector(
    (state: RootState) =>
      state.serviceManagement.loadingState.updateServiceTicketLoading,
  );

  const inverterModelNumbers = useSelector(
    (state: RootState) => state.serviceManagement.inverterModelNumbers,
  );

  const serviceErrorCodes = useSelector(
    (state: RootState) => state.serviceManagement.serviceErrorCodes,
  );

  const statesData = useSelector((state: RootState) => state.kanban.statesData);
  const talukasData = useSelector(
    (state: RootState) => state.kanban.talukasData,
  );

  const detectPhaseFromModel = (model?: string) => {
    if (!model) return "";

    if (inverterModelNumbers.singlePhase?.includes(model)) return "single";
    if (inverterModelNumbers.threePhase?.includes(model)) return "three";

    return "";
  };

  useEffect(() => {
    if (partnerSearchDistrict) {
      dispatch(getTalukasByDistrict(partnerSearchDistrict));
    }
  }, [dispatch, partnerSearchDistrict]);

  const availableTalukas = React.useMemo(() => {
    if (!partnerSearchDistrict || !talukasData) return [];
    return talukasData.taluka || [];
  }, [partnerSearchDistrict, talukasData]);

  useEffect(() => {
    if (!isOpen) {
      setEditMode(false);
      setPartnerDropdownOpen(false);
      setPartnerSearch("");
      setShowPartnerSearch(false);
      setPartnerSearchPincode("");
      setFilteredPartners([]);
      setSelectedPhase("");
      setSelectedErrorCode(null);
    } else {
      // Parse error code if it exists
      if (ticket.errorCode) {
        const match = ticket.errorCode.match(/^(\d+)\s*-\s*(.+)$/);
        if (match) {
          setSelectedErrorCode({
            code: match[1],
            message: match[2],
          });
        }
      }

      // Determine phase from inverter model
      if (ticket.inverterModel) {
        setFormData((prev) => ({
          ...prev,
          inverterModel: ticket.inverterModel,
        }));

        const isSinglePhase = inverterModelNumbers.singlePhase?.includes(
          ticket.inverterModel,
        );
        const isThreePhase = inverterModelNumbers.threePhase?.includes(
          ticket.inverterModel,
        );

        setSelectedPhase(
          isSinglePhase ? "single" : isThreePhase ? "three" : "",
        );
      }

      // Load inverter models and error codes if not already loaded
      if (
        !inverterModelNumbers.singlePhase.length ||
        !inverterModelNumbers.threePhase.length
      ) {
        dispatch(getInverterModelNo());
      }
      if (
        !Object.keys(serviceErrorCodes.singlePhase).length ||
        !Object.keys(serviceErrorCodes.threePhase).length
      ) {
        dispatch(getServiceErrorCodes());
      }
    }
  }, [isOpen, ticket, inverterModelNumbers, serviceErrorCodes, dispatch]);

  const resetModalState = () => {
    setEditMode(false);

    setFormData({
      ticketType: ticket.ticketType,
      epcName: ticket.epcName || "",
      epcMobileNumber: ticket.epcMobileNumber || "",
      epcPincode: ticket.epcPincode || "",
      siteAddress: ticket.siteAddress || "",
      customerName: ticket.customerName || "",
      customerMobileNumber: ticket.customerMobileNumber || "",
      customerAddress: {
        addressLine1: ticket.customerAddress?.addressLine1 || "",
        state: ticket.customerAddress?.state || "",
        district: ticket.customerAddress?.district || "",
        pincode: ticket.customerAddress?.pincode || "",
      },
      serialNumber: ticket.serialNumber || "",
      loggerSerialNo: ticket.loggerSerialNo || "",
      complainNote: ticket.complainNote || "",
      inverterModel: ticket.inverterModel || "",
      errorCode: ticket.errorCode || "",
      servicePartner: ticket.servicePartner,
      resolutionType: ticket.resolutionType || undefined,
    });

    setSelectedPhase(detectPhaseFromModel(ticket.inverterModel));
    setSelectedErrorCode(null);

    setPartnerDropdownOpen(false);
    setPartnerSearch("");
    setShowPartnerSearch(false);
    setPartnerSearchDistrict("");
    setPartnerSearchTaluka("");
    setPartnerSearchPincode("");
    setFilteredPartners([]);
  };

  const handleInputChange = (field: keyof IServiceTicket, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleAddressChange = (field: keyof IAddress, value: string) => {
    setFormData((prev) => ({
      ...prev,
      customerAddress: {
        ...prev.customerAddress!,
        [field]: value,
      },
    }));
  };

  const handlePartnerSearch = async (
    searchType: "pincode" | "district" | "taluka",
    value: string,
  ) => {
    if (searchType === "pincode") {
      setPartnerSearchPincode(value);
      if (value.length === 6) {
        try {
          const result = await dispatch(
            getServicePartnersByFilter({ pincode: value }),
          ).unwrap();
          setFilteredPartners(result || []);
          setPartnerDropdownOpen(true);
        } catch (err) {
          console.error("Error fetching partners:", err);
          setFilteredPartners([]);
        }
      }
    } else if (searchType === "district" && value) {
      setPartnerSearchDistrict(value);
      setPartnerSearchTaluka("");
      try {
        const result = await dispatch(
          getServicePartnersByFilter({ district: value }),
        ).unwrap();
        setFilteredPartners(result || []);
        setPartnerDropdownOpen(true);
      } catch (err) {
        console.error("Error fetching partners:", err);
        setFilteredPartners([]);
      }
    } else if (searchType === "taluka" && value && partnerSearchDistrict) {
      setPartnerSearchTaluka(value);
      try {
        const result = await dispatch(
          getServicePartnersByFilter({
            district: partnerSearchDistrict,
            taluka: value,
          }),
        ).unwrap();
        setFilteredPartners(result || []);
        setPartnerDropdownOpen(true);
      } catch (err) {
        console.error("Error fetching partners:", err);
        setFilteredPartners([]);
      }
    }

    // Clear results if empty
    if (!value || (searchType === "pincode" && value.length === 0)) {
      setFilteredPartners([]);
      setPartnerDropdownOpen(false);
    }
  };

  const handleSelectPartner = (partnerId: string, partnerName: string) => {
    handleInputChange("servicePartner", {
      _id: partnerId,
      name: partnerName,
    } as any);
    setPartnerDropdownOpen(false);
    setPartnerSearch("");
    setShowPartnerSearch(false);
    setPartnerSearchPincode("");
    setPartnerSearchTaluka("");
    setPartnerSearchDistrict("");
  };

  const handleUpdateTicket = async () => {
    try {
      if (!formData.customerName || !formData.customerMobileNumber) {
        toast.error("Customer name and mobile number are required");
        return;
      }

      if (!/^\d{10}$/.test(formData.customerMobileNumber || "")) {
        toast.error("Please enter a valid 10-digit customer mobile number");
        return;
      }

      if (formData.ticketType === ServiceTicketType.inverterFault) {
        if (
          !formData.customerAddress?.addressLine1 ||
          !formData.customerAddress?.state ||
          !formData.customerAddress?.district ||
          !formData.customerAddress?.pincode
        ) {
          toast.error(
            "Complete customer address is required for inverter fault",
          );
          return;
        }

        if (!/^\d{6}$/.test(formData.customerAddress.pincode || "")) {
          toast.error("Please enter a valid 6-digit customer pincode");
          return;
        }

        if (
          formData.epcMobileNumber &&
          !/^\d{10}$/.test(formData.epcMobileNumber)
        ) {
          toast.error("Please enter a valid 10-digit EPC mobile number");
          return;
        }

        if (formData.epcPincode && !/^\d{6}$/.test(formData.epcPincode)) {
          toast.error("Please enter a valid 6-digit EPC pincode");
          return;
        }
      }

      if (!formData.serialNumber) {
        toast.error("Serial number is required");
        return;
      }

      if (formData.ticketType === ServiceTicketType.monitoring) {
        if (!formData.loggerSerialNo) {
          toast.error("Logger serial number is required for type monitoring");
          return;
        }
        if (!formData.inverterModel) {
          toast.error("Inverter model is required for type monitoring");
          return;
        }
      }

      const payload = {
        ...formData,
        errorCode: selectedErrorCode
          ? `${selectedErrorCode.code} - ${selectedErrorCode.message}`
          : formData.errorCode,
        servicePartner: formData.servicePartner?._id
          ? ({ _id: formData.servicePartner._id } as any)
          : undefined,
        complainNote: formData.complainNote ?? "",
      };

      await dispatch(
        updateServiceTicket({
          ticketId: ticket._id as string,
          payload,
        }),
      ).unwrap();

      setEditMode(false);
    } catch (err: any) {
      console.error(err);
      toast.error(err.message || "Failed to update ticket");
    }
  };

  const allDistricts = React.useMemo(() => {
    if (!statesData || statesData.length === 0) return [];

    return Array.from(
      new Set(statesData.flatMap((s) => s.districts || [])),
    ).sort();
  }, [statesData]);

  const availableInverterModels =
    selectedPhase === "single"
      ? inverterModelNumbers.singlePhase
      : selectedPhase === "three"
        ? inverterModelNumbers.threePhase
        : [];

  const isMonitoringTicket =
    formData.ticketType === ServiceTicketType.monitoring;

  const showOnlyMonitoringFields = editMode && isMonitoringTicket;

  const isResolvedColumn = ticket.status?.toLowerCase() === "resolved";

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-slate-900/50 z-50 p-4">
      <div
        className="bg-white w-full max-w-2xl rounded-lg shadow-xl border border-slate-200 max-h-[90vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="border-b border-slate-200 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="bg-slate-100 p-2 rounded">
                <ClipboardList className="w-5 h-5 text-slate-700" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-slate-900">
                  Service Ticket Details
                </h2>
                <div className="flex items-center space-x-4 flex-wrap">
                  <p className="text-xs text-slate-500">
                    TKT-{ticket.ticketNumber}
                  </p>
                  <p className="text-sm text-slate-900">
                    {formatDate(ticket?.createdAt ?? null)}
                  </p>
                </div>
                {ticket.resolvedAt && (
                  <p className="text-sm text-green-700 font-medium">
                    Resolved: {formatDate(ticket?.resolvedAt ?? null)}
                  </p>
                )}
              </div>
            </div>
            <div className="flex items-center space-x-4">
              {!editMode ? (
                <span className="text-sm font-medium text-blue-700">
                  {ticket.ticketType === ServiceTicketType.monitoring
                    ? "Monitoring Issue"
                    : "Inverter Fault"}
                </span>
              ) : (
                <select
                  value={formData.ticketType}
                  onChange={(e) => {
                    const newType = e.target.value as ServiceTicketType;

                    setFormData((prev) => ({
                      ...prev,
                      ticketType: newType,
                    }));
                  }}
                  className="w-full text-xs px-3 py-2 border rounded-lg bg-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
                >
                  <option value={ServiceTicketType.monitoring}>
                    Monitoring Issue
                  </option>
                  <option value={ServiceTicketType.inverterFault}>
                    Inverter Fault
                  </option>
                </select>
              )}

              {!editMode && (
                <button
                  onClick={() => setEditMode(true)}
                  className="px-3 py-1 text-sm rounded-md bg-blue-50 text-blue-600 hover:bg-blue-100"
                >
                  Edit
                </button>
              )}
              {editMode && (
                <button
                  onClick={resetModalState}
                  className="px-3 py-1 text-sm rounded-md bg-gray-50 text-gray-700 hover:bg-gray-100"
                >
                  Cancel
                </button>
              )}
              <button
                onClick={() => {
                  resetModalState();
                  onClose();
                }}
                className="text-slate-400 hover:text-slate-600 transition-colors duration-200 p-2 hover:bg-slate-100 rounded-full"
              >
                <X size={20} />
              </button>
            </div>
          </div>
        </div>

        <div className="max-h-[60vh] overflow-y-auto px-6 py-4">
          <div className="space-y-6">
            {/* EPC Information Section */}
            {!showOnlyMonitoringFields &&
              (ticket.epcName ||
                ticket.epcMobileNumber ||
                ticket.epcPincode ||
                ticket.siteAddress ||
                editMode) && (
                <div>
                  <SectionHeader title="EPC Information" />
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
                    <EditableField
                      icon={<User className="w-4 h-4" />}
                      label="EPC Name"
                      value={ticket.epcName}
                      editValue={formData.epcName}
                      onChange={(value) => handleInputChange("epcName", value)}
                      editMode={editMode}
                    />
                    <EditableField
                      icon={<Phone className="w-4 h-4" />}
                      label="Mobile Number"
                      value={ticket.epcMobileNumber}
                      editValue={formData.epcMobileNumber}
                      onChange={(value) =>
                        handleInputChange("epcMobileNumber", value)
                      }
                      editMode={editMode}
                    />
                    <EditableField
                      icon={<MapPin className="w-4 h-4" />}
                      label="Pincode"
                      value={ticket.epcPincode}
                      editValue={formData.epcPincode}
                      onChange={(value) =>
                        handleInputChange("epcPincode", value)
                      }
                      editMode={editMode}
                    />
                    <EditableField
                      icon={<MapPinned className="w-4 h-4" />}
                      label="Site Address"
                      value={ticket.siteAddress || ""}
                      editValue={formData.siteAddress}
                      onChange={(value) =>
                        handleInputChange("siteAddress", value)
                      }
                      editMode={editMode}
                    />
                  </div>
                </div>
              )}
            {/* Customer Information Section */}
            <div>
              <SectionHeader title="Customer Information" />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
                <EditableField
                  icon={<User className="w-4 h-4" />}
                  label="Customer Name"
                  value={ticket.customerName}
                  editValue={formData.customerName}
                  onChange={(value) => handleInputChange("customerName", value)}
                  required={editMode}
                  editMode={editMode}
                />
                <EditableField
                  icon={<Phone className="w-4 h-4" />}
                  label="Mobile Number"
                  value={ticket.customerMobileNumber}
                  editValue={formData.customerMobileNumber}
                  onChange={(value) =>
                    handleInputChange("customerMobileNumber", value)
                  }
                  required={editMode}
                  editMode={editMode}
                />
                {!showOnlyMonitoringFields && (
                  <>
                    <EditableField
                      icon={<Landmark className="w-4 h-4" />}
                      label="Address Line 1"
                      value={ticket.customerAddress.addressLine1 || ""}
                      editValue={formData.customerAddress?.addressLine1}
                      onChange={(value) =>
                        handleAddressChange("addressLine1", value)
                      }
                      editMode={editMode}
                    />
                    {!editMode ? (
                      <EditableField
                        icon={<MapPinned className="w-4 h-4" />}
                        label="State"
                        value={ticket.customerAddress.state}
                        editMode={false}
                      />
                    ) : (
                      <div className="flex items-start space-x-3 py-3">
                        <div className="flex-shrink-0 w-4 h-4 text-slate-500 mt-0.5">
                          <MapPinned className="w-4 h-4" />
                        </div>
                        <div className="flex-1">
                          <label className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1 block">
                            State <span className="text-red-500">*</span>
                          </label>
                          <select
                            value={formData.customerAddress?.state || ""}
                            onChange={(e) =>
                              setFormData((prev) => ({
                                ...prev,
                                customerAddress: {
                                  ...prev.customerAddress!,
                                  state: e.target.value,
                                  district: "",
                                },
                              }))
                            }
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                          >
                            <option value="">Select State</option>
                            {statesData.map((s) => (
                              <option
                                key={s.state}
                                value={s.state.toLowerCase()}
                              >
                                {s.state.capitalizeWords()}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    )}

                    {!editMode ? (
                      <EditableField
                        icon={<LocateFixed className="w-4 h-4" />}
                        label="District"
                        value={ticket.customerAddress.district}
                        editMode={false}
                      />
                    ) : (
                      <div className="flex items-start space-x-3 py-3">
                        <div className="flex-shrink-0 w-4 h-4 text-slate-500 mt-0.5">
                          <LocateFixed className="w-4 h-4" />
                        </div>
                        <div className="flex-1">
                          <label className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1 block">
                            District <span className="text-red-500">*</span>
                          </label>
                          <select
                            value={formData.customerAddress?.district || ""}
                            disabled={!formData.customerAddress?.state}
                            onChange={(e) =>
                              setFormData((prev) => ({
                                ...prev,
                                customerAddress: {
                                  ...prev.customerAddress!,
                                  district: e.target.value,
                                },
                              }))
                            }
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm disabled:bg-gray-200"
                          >
                            <option value="">Select District</option>
                            {statesData
                              .find(
                                (s) =>
                                  s.state ===
                                  formData.customerAddress?.state.toLowerCase(),
                              )
                              ?.districts?.map((district: string) => (
                                <option key={district} value={district}>
                                  {district.capitalizeWords()}
                                </option>
                              ))}
                          </select>
                        </div>
                      </div>
                    )}

                    <EditableField
                      icon={<MapPin className="w-4 h-4" />}
                      label="Pincode"
                      value={ticket.customerAddress.pincode}
                      editValue={formData.customerAddress?.pincode}
                      onChange={(value) =>
                        handleAddressChange("pincode", value)
                      }
                      editMode={editMode}
                    />
                  </>
                )}
              </div>
            </div>
            {/* Equipment Details Section */}
            {(ticket.loggerSerialNo ||
              ticket.inverterModel ||
              ticket.serialNumber ||
              ticket.errorCode ||
              editMode) && (
              <div>
                <SectionHeader title="Equipment Details" />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
                  <EditableField
                    icon={<QrCode className="w-4 h-4" />}
                    label="Serial Number"
                    value={ticket.serialNumber}
                    editValue={formData.serialNumber}
                    onChange={(value) =>
                      handleInputChange("serialNumber", value)
                    }
                    required={editMode}
                    editMode={editMode}
                  />

                  <EditableField
                    icon={<FileText className="w-4 h-4" />}
                    label="Logger Serial Number"
                    value={ticket.loggerSerialNo || ""}
                    editValue={formData.loggerSerialNo}
                    onChange={(value) =>
                      handleInputChange("loggerSerialNo", value)
                    }
                    required={
                      editMode &&
                      formData.ticketType === ServiceTicketType.monitoring
                    }
                    editMode={editMode}
                  />

                  {!editMode ? (
                    <EditableField
                      icon={<CircuitBoard className="w-4 h-4" />}
                      label="Inverter Model"
                      value={ticket.inverterModel || ""}
                      editMode={editMode}
                    />
                  ) : (
                    <div className="col-span-1 sm:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-4 w-full">
                      {/* LEFT: Select Phase */}
                      <div>
                        <label className="text-gray-600 text-xs font-semibold">
                          Select Phase
                        </label>

                        <div className="flex gap-1 mt-2">
                          <button
                            type="button"
                            onClick={() => {
                              setSelectedPhase("single");
                              handleInputChange("inverterModel", "");
                            }}
                            className={`w-full px-3 py-2 text-xs rounded-lg border ${
                              selectedPhase === "single"
                                ? "bg-blue-100 border-blue-500"
                                : "bg-white border-gray-300"
                            }`}
                          >
                            Single Phase
                          </button>

                          <button
                            type="button"
                            onClick={() => {
                              setSelectedPhase("three");
                              handleInputChange("inverterModel", "");
                            }}
                            className={`w-full px-3 py-2 text-xs rounded-lg border ${
                              selectedPhase === "three"
                                ? "bg-blue-100 border-blue-500"
                                : "bg-white border-gray-300"
                            }`}
                          >
                            Three Phase
                          </button>
                        </div>
                      </div>

                      {/* RIGHT: Select Inverter Model */}
                      <div>
                        <label className="text-gray-600 text-xs font-semibold">
                          Select Model
                        </label>

                        <select
                          value={formData.inverterModel || ""}
                          onChange={(e) =>
                            handleInputChange("inverterModel", e.target.value)
                          }
                          disabled={!selectedPhase}
                          className="w-full text-xs mt-2 px-3 py-2 border rounded-lg bg-white disabled:bg-gray-200"
                        >
                          <option value="">Select Model</option>
                          {availableInverterModels.map((modelNo: string) => (
                            <option key={modelNo} value={modelNo}>
                              {modelNo}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  )}
                </div>
                {!editMode && ticket.errorCode && (
                  <EditableField
                    icon={<AlertTriangle className="w-4 h-4" />}
                    label="Error Status"
                    value={ticket.errorCode || ""}
                    editMode={editMode}
                  />
                )}
              </div>
            )}
            {/* Service Partner Section */}
            <div>
              <SectionHeader title="Assignment" />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
                {!editMode && ticket.assignee && (
                  <EditableField
                    icon={<UserCheck className="w-4 h-4" />}
                    label="Assignee"
                    value={ticket.assignee?.username || "Unassigned"}
                    editMode={false}
                  />
                )}

                {/* Service Partner Selection */}
                {editMode ? (
                  <div className="col-span-1 sm:col-span-2 mt-2 p-4 border rounded-lg bg-gray-50">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-sm font-semibold text-slate-800">
                        Service Partner
                      </h4>
                    </div>

                    <div className="mb-3 space-y-2">
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={partnerSearchPincode}
                          onChange={(e) => {
                            const v = e.target.value.replace(/\D/g, "");
                            if (v.length <= 6)
                              handlePartnerSearch("pincode", v);
                          }}
                          placeholder="Enter 6-digit pincode"
                          className="flex-1 text-xs px-3 py-2 border border-gray-300 rounded-md"
                          maxLength={6}
                        />
                        <button
                          onClick={() => {
                            setPartnerSearchPincode("");
                            setFilteredPartners([]);
                            setPartnerDropdownOpen(false);
                          }}
                          className="px-2 text-xs text-red-600 hover:bg-red-50 rounded-md border border-red-200"
                        >
                          Clear
                        </button>
                      </div>

                      <div className="flex gap-2">
                        <select
                          value={partnerSearchDistrict}
                          onChange={(e) => {
                            setPartnerSearchDistrict(e.target.value);
                            setPartnerSearchTaluka("");
                            handlePartnerSearch("district", e.target.value);
                          }}
                          className="w-full text-xs px-3 py-2 border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="">Search by district</option>
                          {allDistricts.map((d: string) => (
                            <option key={d} value={d.toLowerCase()}>
                              {d.capitalizeWords()}
                            </option>
                          ))}
                        </select>

                        <button
                          onClick={() => {
                            setPartnerSearchDistrict("");
                            setPartnerSearchTaluka("");
                            setFilteredPartners([]);
                            setPartnerDropdownOpen(false);
                          }}
                          className="px-2 text-xs text-red-600 hover:bg-red-50 rounded-md border border-red-200"
                        >
                          Clear
                        </button>
                      </div>
                      {partnerSearchDistrict && (
                        <div className="flex gap-2">
                          <select
                            value={partnerSearchTaluka}
                            onChange={(e) => {
                              setPartnerSearchTaluka(e.target.value);
                              handlePartnerSearch("taluka", e.target.value);
                            }}
                            className="w-full text-xs px-3 py-2 border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                          >
                            <option value="">Search by taluka</option>
                            {availableTalukas.map((taluka: string) => (
                              <option key={taluka} value={taluka.toLowerCase()}>
                                {taluka.capitalizeWords()}
                              </option>
                            ))}
                          </select>

                          <button
                            onClick={() => {
                              setPartnerSearchTaluka("");
                              if (partnerSearchDistrict) {
                                // Refetch with just district
                                handlePartnerSearch(
                                  "district",
                                  partnerSearchDistrict,
                                );
                              }
                            }}
                            className="px-2 text-xs text-red-600 hover:bg-red-50 rounded-md border border-red-200"
                          >
                            Clear
                          </button>
                        </div>
                      )}
                    </div>

                    <div className="relative">
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => setPartnerDropdownOpen((p) => !p)}
                          className="w-full text-xs px-3 py-2 border border-gray-300 rounded-lg bg-white text-left flex items-center justify-between"
                        >
                          <span
                            className={
                              formData.servicePartner?.name
                                ? "text-gray-800"
                                : "text-gray-500"
                            }
                          >
                            {formData.servicePartner?.name || "Select Partner"}
                          </span>

                          {partnerDropdownOpen ? (
                            <ChevronUp className="w-4 h-4 text-gray-500" />
                          ) : (
                            <ChevronDown className="w-4 h-4 text-gray-500" />
                          )}
                        </button>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleInputChange("servicePartner", null);
                          }}
                          className="px-2 text-xs text-red-600 hover:bg-red-50 rounded-md border border-red-200"
                        >
                          Clear
                        </button>
                      </div>

                      {partnerDropdownOpen && (
                        <>
                          <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-auto">
                            <div className="p-2 border-b border-gray-200">
                              <input
                                type="text"
                                placeholder="Search by name..."
                                value={partnerSearch}
                                onChange={(e) =>
                                  setPartnerSearch(e.target.value)
                                }
                                className="w-full text-xs px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
                                autoFocus
                              />
                            </div>

                            <div className="py-1">
                              {filteredPartners.length ? (
                                filteredPartners
                                  .filter((p) =>
                                    p.name
                                      .toLowerCase()
                                      .includes(partnerSearch.toLowerCase()),
                                  )
                                  .map((partner) => (
                                    <button
                                      key={partner._id}
                                      onClick={() =>
                                        handleSelectPartner(
                                          partner._id,
                                          partner.name,
                                        )
                                      }
                                      className={`w-full text-xs px-3 py-2 text-left hover:bg-gray-100 ${
                                        formData.servicePartner?._id ===
                                        partner._id
                                          ? "bg-blue-50 text-blue-600"
                                          : "text-gray-800"
                                      }`}
                                    >
                                      {partner.name}
                                    </button>
                                  ))
                              ) : (
                                <div className="px-3 py-2 text-xs text-gray-500 text-center">
                                  No partners found
                                </div>
                              )}
                            </div>
                          </div>

                          <div
                            className="fixed inset-0 z-0"
                            onClick={() => {
                              setPartnerDropdownOpen(false);
                              setPartnerSearch("");
                            }}
                          />
                        </>
                      )}
                    </div>
                  </div>
                ) : (
                  <EditableField
                    icon={<Users className="w-4 h-4" />}
                    label="Service Partner"
                    value={ticket.servicePartner?.name || ""}
                    editMode={editMode}
                  />
                )}
              </div>
            </div>
            {/* Resolution Type */}
            <div className="space-y-0">
              {(ticket.resolutionType || editMode) && (
                <div>
                  <SectionHeader title="Additional Information" />

                  <EditableField
                    icon={<AlertTriangle className="w-4 h-4" />}
                    label="Resolution Type"
                    value={ticket.resolutionType || ""}
                    editValue={formData.resolutionType}
                    onChange={(value) =>
                      handleInputChange("resolutionType", value)
                    }
                    type="select"
                    options={[
                      { value: "replaced", label: "Replaced" },
                      { value: "repaired", label: "Repaired" },
                    ]}
                    editMode={editMode && !isResolvedColumn}
                  />
                </div>
              )}
              {/* Complain Note Section */}
              {(ticket.complainNote || editMode) && (
                <div>
                  <EditableField
                    icon={<MessageSquareWarning className="w-4 h-4" />}
                    label="Complain Note"
                    value={ticket.complainNote ?? ""}
                    editValue={formData.complainNote}
                    onChange={(value) =>
                      handleInputChange("complainNote", value)
                    }
                    type="textarea"
                    editMode={editMode}
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="border-t border-slate-200 px-6 py-4 bg-slate-50">
          <div className="flex justify-end space-x-3">
            <Button
              type={ButtonType.Cancel}
              onClick={() => {
                resetModalState();
                onClose();
              }}
              className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-800 hover:bg-slate-200 transition-colors duration-200 rounded"
            >
              Close
            </Button>
            {editMode && (
              <Button
                type={ButtonType.Submit}
                onClick={handleUpdateTicket}
                className="px-4 py-2 text-sm font-medium"
                disabled={updateServiceTicketLoading}
              >
                {updateServiceTicketLoading ? "Saving..." : "Update Ticket"}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServiceTicketCardModal;
